// File: src/components/HowItWorks.jsx
import React, { useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Plug, Activity, BarChart2 } from 'lucide-react';
import { AuthContext } from '../../context/AuthContext';
import useData from '../../hooks/useData';

export default function HowItWorks() {
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useContext(AuthContext);
  const API = process.env.REACT_APP_API_URL;
  
  // Check if we were redirected here from HomePage
  const wasRedirectedFromHome = location.state?.from === 'home';
  
  // Fetch sites data to verify if user actually has sites
  const { data: sites, loading: sitesLoading } = useData(
    token ? `sites:${token}` : 'noop',
    () => fetch(`${API}/api/sites`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(r => {
      if (!r.ok) throw new Error(r.status);
      return r.json();
    }),
    { ttlMs: 24 * 60 * 60 * 1000, deps: [token] }
  );

  React.useEffect(() => {
    // If we were explicitly redirected from HomePage, trust that decision
    if (wasRedirectedFromHome) {
      console.log('HowItWorks: Was redirected from home, staying on page');
      return;
    }
    
    // Only check for redirect if we have loaded sites data
    if (!sitesLoading && sites && Array.isArray(sites)) {
      if (sites.length > 0) {
        console.log('HowItWorks: User has sites, redirecting to home');
        navigate('/home', { replace: true });
      }
    }
  }, [navigate, sites, sitesLoading, wasRedirectedFromHome]);

  // Show loading while checking sites
  if (sitesLoading && !wasRedirectedFromHome) {
    console.log('HowItWorks: Loading sites data');
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        flexDirection: 'column',
        gap: '20px'
      }}>
        <div style={{
          border: '4px solid #f3f3f3',
          borderTop: '4px solid #007bff',
          borderRadius: '50%',
          width: '40px',
          height: '40px',
          animation: 'spin 1s linear infinite'
        }}></div>
        <p>Loading...</p>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  console.log('HowItWorks: Rendering main content');

  const steps = [
    {
      number: '1',
      title: 'Connect Your Store',
      desc: 'Easily integrate with Shopify, WooCommerce, or BigCommerce in under 2 minutes.',
      icon: <Plug size={32} />
    },
    {
      number: '2',  
      title: 'Track User Behavior',
      desc: 'We automatically capture every click, scroll, and session in real time—no coding needed.',
      icon: <Activity size={32} />
    },
    {
      number: '3',
      title: 'See What\'s Working',
      desc: 'Visualize heatmaps, replay sessions, and uncover which parts of your site convert—or don\'t.',
      icon: <BarChart2 size={32} />
    },
  ];

  // Rest of your component remains the same...
  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #00a8a9 0%, #6a0dad 100%)',
      padding: '40px 20px'
    }}>
      {/* Hero Section */}
      <div style={{
        textAlign: 'center',
        marginBottom: '60px',
        color: 'white'
      }}>
        <div style={{ marginBottom: '30px' }}>
          <img
            src={`${process.env.PUBLIC_URL}/AdobeStock_133441382.png`}
            alt="BaoBrain Logo"
            style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
            }}
          />
        </div>
        <h1 style={{
          fontSize: '3rem',
          fontWeight: '700',
          margin: '0 0 20px 0',
          textShadow: '0 2px 4px rgba(0,0,0,0.3)'
        }}>
          How BaoBrain Works
        </h1>
        <p style={{
          fontSize: '1.2rem',
          margin: '0',
          opacity: '0.9'
        }}>
          Quick setup. Powerful insights. Smarter growth.
        </p>
      </div>

      {/* Steps Section */}
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: '40px',
        marginBottom: '60px'
      }}>
        {steps.map((step, idx) => (
          <div
            key={step.number}
            style={{
              background: 'white',
              borderRadius: '16px',
              padding: '40px 30px',
              textAlign: 'center',
              boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
              transform: 'translateY(0)',
              transition: 'transform 0.3s ease, box-shadow 0.3s ease',
              cursor: 'pointer'
            }}
            onMouseEnter={e => {
              e.currentTarget.style.transform = 'translateY(-8px)';
              e.currentTarget.style.boxShadow = '0 20px 60px rgba(0,0,0,0.15)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 10px 40px rgba(0,0,0,0.1)';
            }}
          >
            <div style={{
              color: '#667eea',
              marginBottom: '20px',
              display: 'flex',
              justifyContent: 'center'
            }}>
              {step.icon}
            </div>
            
            <div style={{
              background: '#667eea',
              color: 'white',
              borderRadius: '50px',
              padding: '8px 16px',
              fontWeight: '600',
              fontSize: '0.9rem',
              marginBottom: '20px',
              display: 'inline-block'
            }}>
              Step {step.number}
            </div>
            
            <h3 style={{
              fontSize: '1.5rem',
              fontWeight: '600',
              margin: '0 0 15px 0',
              color: '#333'
            }}>
              {step.title}
            </h3>
            
            <p style={{
              color: '#666',
              lineHeight: '1.6',
              margin: '0'
            }}>
              {step.desc}
            </p>
          </div>
        ))}
      </div>

      {/* Call-to-Action */}
      <div style={{
        textAlign: 'center'
      }}>
        <button
          onClick={() => {
            console.log('Get Started clicked, navigating to /sites');
            navigate('/sites');
          }}
          style={{
            background: 'white',
            color: '#667eea',
            border: 'none',
            padding: '16px 40px',
            fontSize: '1.1rem',
            fontWeight: '600',
            borderRadius: '50px',
            cursor: 'pointer',
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
            transition: 'transform 0.2s ease, box-shadow 0.2s ease'
          }}
          onMouseEnter={e => {
            e.target.style.transform = 'translateY(-2px)';
            e.target.style.boxShadow = '0 12px 40px rgba(0,0,0,0.25)';
          }}
          onMouseLeave={e => {
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = '0 8px 32px rgba(0,0,0,0.2)';
          }}
        >
          Get Started Now →
        </button>
      </div>
    </div>
  );
}